import { getBrand } from 'flow/brands/brands';
import { fromJS, List, Map } from 'immutable';

const WALLBOARD_ENTITYKEY_PREFIX = 'wallboard-';

export const DATA_PERIOD_RESETS_AT_MIDNIGHT = 'resetsAtMidnight';
export const DATA_PERIOD_TYPE_DAILY = 'daily';
export const DATA_PERIOD_TYPE_CUSTOM = 'custom';
export const DATA_PERIOD_24_HOURS_IN_SECONDS = 24 * 3600;
export const SERVICE_LEVEL_PERIOD_DEFAULT = 120;
export const SERVICE_LEVEL_WARNING_THRESHOLD_DEFAULT = 95;

export const wallboardTypes = Object.freeze({
    HUNT_GROUPS_MULTIPLE: 'hunt_groups_multiple',
    HUNT_GROUP_TEAM: 'hunt_group_team',
    HUNT_GROUP_INSIGHT: 'hunt_group_insight',
    TEAM_CUSTOM: 'team_custom',
});

export const customFieldsTypes = Object.freeze({
    ANSWERED: 'answered',
    OUTGOING: 'outgoing',
    MISSED: 'missed',
    AVERAGE_ANSWER_TIME: 'average-answer-time',
    ANSWER_RATIO: 'answer-ratio',
    AVERAGE_TIME_PER_CALL: 'average-time-per-call',
});

export const huntGroupsMultipleFieldsTypes = Object.freeze({
    AGENTS_LOGGED_IN: 'agents-logged-in',
    CALLS_IN_QUEUE: 'calls-in-queue',
    CURRENT_WAIT_TIME: 'current-wait-time',
    RECEIVED_CALLS: 'received-calls',
    ABANDONED_CALLS: 'abandoned-calls',
    SERVICE_LEVEL: 'service-level',
});

// Uncomment when more templates are available
export const getWallboardTypes = () => {
    return List([
        wallboardTypes.HUNT_GROUPS_MULTIPLE,
        // wallboardTypes.HUNT_GROUP_TEAM,
        // wallboardTypes.HUNT_GROUP_INSIGHT,
        wallboardTypes.TEAM_CUSTOM,
    ]);
};

export const getWallboardTypeFromUrlPathKey = (urlPathKey) => {
    switch (urlPathKey) {
        case 'team':
            return wallboardTypes.HUNT_GROUP_TEAM;
        case 'insight':
            return wallboardTypes.HUNT_GROUP_INSIGHT;
        case 'groups':
            return wallboardTypes.HUNT_GROUPS_MULTIPLE;
        case 'team-custom':
            return wallboardTypes.TEAM_CUSTOM;
        default:
            return wallboardTypes.HUNT_GROUP_TEAM;
    }
};

export const getWallboardUrlPathKeyFromBoardType = (boardType) => {
    switch (boardType) {
        case wallboardTypes.HUNT_GROUP_TEAM:
            return 'team';
        case wallboardTypes.HUNT_GROUP_INSIGHT:
            return 'insight';
        case wallboardTypes.HUNT_GROUPS_MULTIPLE:
            return 'groups';
        case wallboardTypes.TEAM_CUSTOM:
            return 'team-custom';
        default:
            return 'team';
    }
};

export const getWallboardUrl = (urlPath) => {
    const brandWallboardUrl = getBrand().brandWallboardUrl;
    if (!brandWallboardUrl) {
        return null; // Return null to indicate wallboard is not available
    }
    return `${brandWallboardUrl}${urlPath}`;
};

export const MAX_QUEUES_AMOUNT_WALLBOARD = 18;
export const MAX_AGENTS_AMOUNT_WALLBOARD = 18;
export const getWallboardIdFromWallboardEntitytKey = (entityKey) => {
    if (
        typeof entityKey !== 'string' ||
        !entityKey.includes(WALLBOARD_ENTITYKEY_PREFIX)
    ) {
        return undefined;
    }

    try {
        const idString = entityKey.substring(entityKey.lastIndexOf('-') + 1);
        return Number(idString);
    } catch (_error) {
        return undefined;
    }
};

export const getDefaultSettingsComponentFriendly = () =>
    Map({
        dataPeriodType: DATA_PERIOD_TYPE_DAILY,
        dataPeriod: DATA_PERIOD_24_HOURS_IN_SECONDS,
        serviceLevelPeriod: SERVICE_LEVEL_PERIOD_DEFAULT,
        serviceLevelWarningThreshold: SERVICE_LEVEL_WARNING_THRESHOLD_DEFAULT,
    });

export const getDefaultTeamCustomSettingsComponentFriendly = () =>
    Map({
        dataPeriodType: DATA_PERIOD_TYPE_DAILY,
        dataPeriod: DATA_PERIOD_24_HOURS_IN_SECONDS,
    });

export const wallboardDataIsSufficientForSaving = (wallboardData) => {
    if (wallboardData.get('type') === wallboardTypes.TEAM_CUSTOM) {
        return !!(
            wallboardData &&
            wallboardData.get('name') &&
            wallboardData.get('agentKeys') &&
            wallboardData.get('agentKeys').size
        );
    }

    return !!(
        wallboardData &&
        wallboardData.get('name') &&
        wallboardData.get('queueKeys') &&
        wallboardData.get('queueKeys').size
    );
};

const wallboardHasDataPeriodTypeDaily = (data) => {
    if (!data) {
        return false;
    }

    return (
        data.getIn(['settings', 'dataPeriodType']) === DATA_PERIOD_TYPE_DAILY
    );
};

export const generateWallboardDTOFromComponentFriendlyData = (
    wallboardData,
) => {
    const boardType = wallboardData.get('type');
    let settings = wallboardData.get('settings');

    if (wallboardHasDataPeriodTypeDaily(wallboardData)) {
        settings = settings
            .set(DATA_PERIOD_RESETS_AT_MIDNIGHT, true)
            .set('dataPeriod', DATA_PERIOD_24_HOURS_IN_SECONDS)
            .delete('dataPeriodType');
    } else {
        settings = settings
            .set(DATA_PERIOD_RESETS_AT_MIDNIGHT, false)
            .delete('dataPeriodType');
    }

    let wallboardDTO = Map({
        name: wallboardData.get('name'),
        settings: settings,
        type: boardType,
    });

    if (boardType === wallboardTypes.TEAM_CUSTOM) {
        wallboardDTO = wallboardDTO.set(
            'agentKeys',
            wallboardData.get('agentKeys'),
        );
    } else {
        wallboardDTO = wallboardDTO.set(
            'queueKeys',
            wallboardData.get('queueKeys'),
        );
    }

    return wallboardDTO;
};

export const getWallboardCreateComponentFriendlyWallboardData = (
    wallboardDTO,
    boardType,
) => {
    if (wallboardDTO) {
        return getWallboardCreateComponentFriendlyDataFromExistingWallboard(
            wallboardDTO,
        );
    } else {
        return getWallboardCreateComponentFriendlyData(boardType);
    }
};

const dataPeriodSettingsTranslatesToTypeDaily = (settings) => {
    return (
        settings.get(DATA_PERIOD_RESETS_AT_MIDNIGHT) === true &&
        settings.get('dataPeriod') === DATA_PERIOD_24_HOURS_IN_SECONDS
    );
};

const getComponentFriendlySettingsDataFromExistingSettings = (settings) => {
    let settingsToReturn = settings;

    if (dataPeriodSettingsTranslatesToTypeDaily(settingsToReturn)) {
        settingsToReturn = settingsToReturn.set(
            'dataPeriodType',
            DATA_PERIOD_TYPE_DAILY,
        );
    } else {
        settingsToReturn = settingsToReturn.set(
            'dataPeriodType',
            DATA_PERIOD_TYPE_CUSTOM,
        );
    }

    const dataPeriod = getValidDataPeriodValue(
        settingsToReturn.get('dataPeriod'),
    );
    settingsToReturn = settingsToReturn.set('dataPeriod', dataPeriod);

    return settingsToReturn;
};

const getWallboardCreateComponentFriendlyDataFromExistingWallboard = (
    wallboardDTO,
) => {
    const boardType = wallboardDTO.get('type');
    const settingsDoNotExist =
        !wallboardDTO.get('settings') || wallboardDTO.get('settings').isEmpty();
    const settings = settingsDoNotExist
        ? getWallboardDefaultSettings(boardType)
        : getComponentFriendlySettingsDataFromExistingSettings(
              wallboardDTO.get('settings'),
          );

    return Map({
        queueKeys: wallboardDTO.get('queueKeys'),
        agentKeys: wallboardDTO.get('agentKeys'),
        name: wallboardDTO.get('name'),
        key: wallboardDTO.get('key'),
        type: boardType,
        settings: settings,
    });
};

const getWallboardCreateComponentFriendlyData = (boardType) => {
    const defaultSettings = getWallboardDefaultSettings(boardType);
    return Map({ settings: defaultSettings, type: boardType });
};

export const getWallboardDefaultSettings = (boardType) => {
    return boardType === wallboardTypes.TEAM_CUSTOM
        ? getDefaultTeamCustomSettingsComponentFriendly()
        : getDefaultSettingsComponentFriendly();
};

export const getValidDataPeriodValueInSecondsFromHours = (valueHours) => {
    if (isNaN(valueHours)) {
        return 0;
    }

    const valueSeconds = valueHours * 3600;
    return getValidDataPeriodValue(valueSeconds);
};

export const getValidDataPeriodValue = (valueSeconds) => {
    /**
     * Used so that dataPeriods for old dashboards with a value potentially higher than 24 hours
     * are tweaked down to 24 hours, which is the ceiling for how far back we allow data to be fetched.
     */
    return valueSeconds > DATA_PERIOD_24_HOURS_IN_SECONDS
        ? DATA_PERIOD_24_HOURS_IN_SECONDS
        : valueSeconds;
};

export const getWallboardWithSettingsCastToProperDataTypes = (wallboard) => {
    if (
        !wallboard ||
        !wallboard.get('settings') ||
        wallboard.get('settings').isEmpty()
    ) {
        return wallboard;
    }

    let settings = wallboard.get('settings');
    const dataPeriodParsed = parseInt(settings.get('dataPeriod'));
    const serviceLevelPeriodParsed = parseInt(
        settings.get('serviceLevelPeriod'),
    );
    const serviceLevelWarningThresholdParsed = parseInt(
        settings.get('serviceLevelWarningThreshold'),
    );
    const resetsAtMidnightParsed =
        settings.get(DATA_PERIOD_RESETS_AT_MIDNIGHT) === 'true' ||
        settings.get(DATA_PERIOD_RESETS_AT_MIDNIGHT) === true;

    if (!isNaN(dataPeriodParsed)) {
        settings = settings.set('dataPeriod', dataPeriodParsed);
    }

    if (!isNaN(serviceLevelPeriodParsed)) {
        settings = settings.set('serviceLevelPeriod', serviceLevelPeriodParsed);
    }

    if (!isNaN(serviceLevelWarningThresholdParsed)) {
        settings = settings.set(
            'serviceLevelWarningThreshold',
            serviceLevelWarningThresholdParsed,
        );
    }

    settings = settings.set(
        DATA_PERIOD_RESETS_AT_MIDNIGHT,
        resetsAtMidnightParsed,
    );

    return wallboard.set('settings', settings);
};

export const getWallboardsWithSettingsCastToProperDataTypes = (wallboards) => {
    if (!wallboards || wallboards.size === 0) {
        return wallboards;
    }

    let wallboardsToReturn = List();

    wallboards.forEach((wb) => {
        wallboardsToReturn = wallboardsToReturn.push(
            getWallboardWithSettingsCastToProperDataTypes(wb),
        );
    });

    return wallboardsToReturn;
};

export const getSettingDataPeriodDescription = (i18next, useOmniWording) => {
    const i18Addition = useOmniWording ? '.interactions' : '';
    return i18next.t(`wallboard.settings.dataperiod${i18Addition}.description`);
};

export const getCustomFieldsForTeamCustom = (i18next, useOmniWording) => {
    const i18Addition = useOmniWording ? '.interactions' : '';
    const i18AdditionPerType = useOmniWording ? 'interaction' : 'call';

    return fromJS([
        {
            key: customFieldsTypes.ANSWERED,
            value: i18next.t(`wallboard.custom_field.answered${i18Addition}`),
            description: i18next.t(
                `wallboard.custom_field.answered${i18Addition}.description`,
            ),
            selected: true,
        },
        {
            key: customFieldsTypes.OUTGOING,
            value: i18next.t(`wallboard.custom_field.outgoing${i18Addition}`),
            description: i18next.t(
                `wallboard.custom_field.outgoing${i18Addition}.description`,
            ),
            selected: true,
        },
        {
            key: customFieldsTypes.MISSED,
            value: i18next.t('wallboard.custom_field.missed'),
            description: i18next.t('wallboard.custom_field.missed.description'),
            selected: true,
        },
        {
            key: customFieldsTypes.AVERAGE_ANSWER_TIME,
            value: i18next.t('wallboard.custom_field.average_answer_time'),
            description: i18next.t(
                `wallboard.custom_field.average_answer_time${i18Addition}.description`,
            ),
            selected: true,
        },
        {
            key: customFieldsTypes.ANSWER_RATIO,
            value: i18next.t('wallboard.custom_field.answer_ratio'),
            description: i18next.t(
                `wallboard.custom_field.answer_ratio${i18Addition}.description`,
            ),
            selected: true,
        },
        {
            key: customFieldsTypes.AVERAGE_TIME_PER_CALL,
            value: i18next.t(
                `wallboard.custom_field.average_time_per_${i18AdditionPerType}`,
            ),
            description: i18next.t(
                `wallboard.custom_field.average_time_per_${i18AdditionPerType}.description`,
            ),
            selected: true,
        },
    ]);
};

export const getCustomFieldsForHuntGroupsMultiple = (
    i18next,
    useOmniWording,
    serviceLevelPeriod,
) => {
    const i18Addition = useOmniWording ? '.interactions' : '';
    const i18AdditionPerTypePlural = useOmniWording ? 'interactions' : 'calls';

    return fromJS([
        {
            key: huntGroupsMultipleFieldsTypes.AGENTS_LOGGED_IN,
            value: i18next.t(
                'wallboard.hunt_groups_multiple_field.agents_logged_in',
            ),
            description: i18next.t(
                'wallboard.hunt_groups_multiple_field.agents_logged_in.description',
            ),
            selected: true,
        },
        {
            key: huntGroupsMultipleFieldsTypes.CALLS_IN_QUEUE,
            value: i18next.t(
                `wallboard.hunt_groups_multiple_field.${i18AdditionPerTypePlural}_in_queue`,
            ),
            description: i18next.t(
                `wallboard.hunt_groups_multiple_field.${i18AdditionPerTypePlural}_in_queue.description`,
            ),
            selected: true,
        },
        {
            key: huntGroupsMultipleFieldsTypes.CURRENT_WAIT_TIME,
            value: i18next.t(
                'wallboard.hunt_groups_multiple_field.current_wait_time',
            ),
            description: i18next.t(
                `wallboard.hunt_groups_multiple_field.current_wait_time${i18Addition}.description`,
            ),
            selected: true,
        },
        {
            key: huntGroupsMultipleFieldsTypes.RECEIVED_CALLS,
            value: i18next.t(
                `wallboard.hunt_groups_multiple_field.received_${i18AdditionPerTypePlural}`,
            ),
            description: i18next.t(
                `wallboard.hunt_groups_multiple_field.received_${i18AdditionPerTypePlural}.description`,
            ),
            selected: true,
        },
        {
            key: huntGroupsMultipleFieldsTypes.ABANDONED_CALLS,
            value: i18next.t(
                'wallboard.hunt_groups_multiple_field.abandoned_calls',
            ),
            description: i18next.t(
                'wallboard.hunt_groups_multiple_field.abandoned_calls.description',
            ),
            selected: true,
        },
        {
            key: huntGroupsMultipleFieldsTypes.SERVICE_LEVEL,
            value: i18next.t(
                'wallboard.hunt_groups_multiple_field.service_level',
            ),
            description: i18next.t(
                `wallboard.hunt_groups_multiple_field.service_level${i18Addition}.description`,
                { period: serviceLevelPeriod },
            ),
            selected: true,
        },
    ]);
};
